import { isTabletDown } from '../Utility/Breakpoint'
import { getElement, getElementChild, getElements } from '../Utility/Dom'
import { IS_ACTIVE } from '../Utility/State'

interface PanelHistoryEntry {
  panelId: string
  trigger: HTMLElement | null
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

class NavMainMobile {
  private readonly backButtons: NodeListOf<HTMLElement>
  private readonly breadcrumbButtons: NodeListOf<HTMLElement>
  private readonly navButton: HTMLElement | null
  private panelHistory: PanelHistoryEntry[] = []
  private readonly panels: NodeListOf<HTMLElement>
  private readonly panelTriggers: NodeListOf<HTMLElement>

  constructor() {
    this.navButton = getElement('nav-button')
    this.panels = getElements('nav-panel')
    this.panelTriggers = document.querySelectorAll('[data-panel-target]')
    this.backButtons = getElements('nav-panel-back')
    this.breadcrumbButtons = getElements('nav-main-breadcrumb-button')
  }

  public init(): void {
    if (this.navButton === null || this.panels.length === 0) {
      return
    }

    this.addEvents()
    this.updatePanelInertState()
    this.updateNavTopOffset()
  }

  private addEvents(): void {
    this.navButton?.addEventListener('click', (): void => {
      this.toggleNav()
    })

    this.panelTriggers.forEach((trigger): void => {
      trigger.addEventListener('click', (event): void => {
        if (isTabletDown() === false) {
          return
        }

        event.preventDefault()
        const targetId = trigger.getAttribute('data-panel-target')

        if (targetId !== null) {
          this.navigateToPanel(targetId, trigger)
        }
      })
    })

    this.backButtons.forEach((button): void => {
      button.addEventListener('click', (): void => {
        this.navigateBack()
      })
    })

    this.breadcrumbButtons.forEach((button): void => {
      button.addEventListener('click', (): void => {
        if (isTabletDown() === false) {
          return
        }

        const targetId = button.getAttribute('data-back-target')

        if (targetId !== null) {
          this.navigateToBreadcrumbTarget(targetId)
        } else {
          this.navigateToLevel1()
        }
      })
    })

    document.addEventListener('keydown', (event): void => {
      if (event.key === 'Escape' && this.isNavOpen()) {
        this.closeNav()
      }
    })

    window.addEventListener('resize', (): void => {
      if (isTabletDown() === false && this.isNavOpen()) {
        this.closeNav()
      }
      this.updatePanelInertState()
      this.updateNavTopOffset()
    })
  }

  private closeNav(): void {
    this.navButton?.setAttribute('aria-expanded', 'false')
    this.resetToLevel1()
    this.updatePanelInertState()
    this.setPageInert(false)
    this.navButton?.focus()
  }

  private focusFirstElement(container: HTMLElement | null): void {
    if (container === null) {
      return
    }

    const focusable = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
    focusable?.focus()
  }

  private getActivePanel(): HTMLElement | null {
    for (const panel of this.panels) {
      if (panel.classList.contains(IS_ACTIVE) && panel.getAttribute('data-level') !== '1') {
        return panel
      }
    }

    return this.getLevel1Panel()
  }

  private getLevel1Panel(): HTMLElement | null {
    return document.querySelector('[data-js="nav-panel"][data-level="1"]')
  }

  private isNavOpen(): boolean {
    return this.navButton?.getAttribute('aria-expanded') === 'true'
  }

  private navigateBack(): void {
    const currentPanel = this.getActivePanel()

    if (currentPanel !== null) {
      currentPanel.classList.remove(IS_ACTIVE)
    }

    const previousEntry = this.panelHistory.pop()

    if (previousEntry && previousEntry.panelId !== 'level-1') {
      const previousPanel = document.getElementById(previousEntry.panelId)

      if (previousPanel !== null) {
        previousPanel.classList.add(IS_ACTIVE)
        this.updatePanelInertState()

        requestAnimationFrame((): void => {
          if (previousEntry.trigger !== null) {
            previousEntry.trigger.focus()
          } else {
            this.focusFirstElement(previousPanel)
          }
        })
        return
      }
    }

    this.resetToLevel1()

    requestAnimationFrame((): void => {
      if (previousEntry?.trigger) {
        previousEntry.trigger.focus()
      } else {
        const level1Panel = this.getLevel1Panel()
        this.focusFirstElement(level1Panel)
      }
    })
  }

  private navigateToBreadcrumbTarget(targetId: string): void {
    const targetPanel = document.getElementById(targetId)

    if (targetPanel === null) {
      return
    }

    const currentPanel = this.getActivePanel()

    if (currentPanel !== null) {
      currentPanel.classList.remove(IS_ACTIVE)
    }

    while (this.panelHistory.length > 0) {
      const entry = this.panelHistory[this.panelHistory.length - 1]

      if (entry.panelId === targetId) {
        this.panelHistory.pop()
        break
      }

      this.panelHistory.pop()
    }

    targetPanel.classList.add(IS_ACTIVE)
    this.updatePanelInertState()

    requestAnimationFrame((): void => {
      this.focusFirstElement(targetPanel)
    })
  }

  private navigateToLevel1(): void {
    const currentPanel = this.getActivePanel()

    if (currentPanel !== null) {
      currentPanel.classList.remove(IS_ACTIVE)
    }

    this.resetToLevel1()

    requestAnimationFrame((): void => {
      const level1Panel = this.getLevel1Panel()
      this.focusFirstElement(level1Panel)
    })
  }

  private navigateToPanel(targetId: string, trigger: HTMLElement): void {
    const targetPanel = document.getElementById(targetId)

    if (targetPanel === null) {
      return
    }

    const currentPanel = this.getActivePanel()

    if (currentPanel !== null) {
      this.panelHistory.push({
        panelId: currentPanel.id || 'level-1',
        trigger
      })
      currentPanel.classList.remove(IS_ACTIVE)
    }

    targetPanel.classList.add(IS_ACTIVE)
    this.updatePanelInertState()

    requestAnimationFrame((): void => {
      const backButton = getElementChild(targetPanel, 'nav-panel-back')

      if (backButton !== null) {
        backButton.focus()
      } else {
        this.focusFirstElement(targetPanel)
      }
    })
  }

  private openNav(): void {
    this.navButton?.setAttribute('aria-expanded', 'true')
    this.updateNavTopOffset()
    this.updatePanelInertState()
    this.setPageInert(true)

    requestAnimationFrame((): void => {
      const level1Panel = this.getLevel1Panel()
      this.focusFirstElement(level1Panel)
    })
  }

  private resetToLevel1(): void {
    this.panels.forEach((panel): void => {
      if (panel.getAttribute('data-level') === '1') {
        panel.classList.add(IS_ACTIVE)
      } else {
        panel.classList.remove(IS_ACTIVE)
      }
    })

    this.panelHistory = []
    this.updatePanelInertState()
  }

  // Trap focus inside nav by making siblings inert while open
  private setPageInert(inert: boolean): void {
    const nav = getElement('nav-main')
    const inertElements = document.querySelectorAll('main, footer, [data-inert]')

    inertElements.forEach((element): void => {
      if (inert) {
        element.setAttribute('inert', '')
      } else {
        element.removeAttribute('inert')
      }
    })

    const header = nav?.closest('header')

    if (header) {
      Array.from(header.children).forEach((child): void => {
        if (child !== nav && child.contains(nav) === false) {
          if (inert) {
            child.setAttribute('inert', '')
          } else {
            child.removeAttribute('inert')
          }
        }
      })
    }
  }

  private toggleNav(): void {
    if (this.isNavOpen()) {
      this.closeNav()
    } else {
      this.openNav()
    }
  }

  private updateNavTopOffset(): void {
    if (isTabletDown() === false) {
      return
    }

    const entrypoint = getElement('entrypoint')
    const topBar = getElement('top-bar')
    const offset = entrypoint?.getBoundingClientRect().top ?? topBar?.getBoundingClientRect().bottom

    if (offset === undefined) {
      return
    }

    document.documentElement.style.setProperty('--mobile-nav-top', `${Math.max(0, offset)}px`)
  }

  private updatePanelInertState(): void {
    if (isTabletDown() === false) {
      this.panels.forEach((panel): void => {
        panel.removeAttribute('inert')
      })
      return
    }

    this.panels.forEach((panel): void => {
      if (panel.classList.contains(IS_ACTIVE)) {
        panel.removeAttribute('inert')
      } else {
        panel.setAttribute('inert', '')
      }
    })
  }
}

const navMainMobile = new NavMainMobile()
navMainMobile.init()
